import Link from "next/link";
import React from "react";
import JsonLd from "@/components/JsonLd";
import SiteShell, { APP_URL } from "@/components/SiteShell";
import {
  SCRIPT_ITEM_META,
  type ScriptItemKind,
} from "@/funnel/salesScript";
import { PLAN_LIMITS } from "@/lib/planLimits";
import { SITE_URL, faqLd, salesScriptHowToLd } from "@/lib/structuredData";

/**
 * /sales-scripts — the feature page for Sales Scripts (shipped v297/v298).
 *
 * Note the deliberate same-path split: app.frazzlecrm.com/sales-scripts is
 * the in-app ledger (src/pages/sales-scripts.tsx); the marketing host
 * rewrites the same path here. Host routing in src/middleware.ts keeps them
 * apart — one URL, one meaning per audience.
 *
 * The card tags, colours and one-line hints are imported from the product's
 * own SCRIPT_ITEM_META, and the per-plan card cap from PLAN_LIMITS, so this
 * page cannot drift away from what the app actually does. The example script
 * below is written for the page; it is not a customer's.
 */

type Card = { kind: ScriptItemKind; text: string };
type Branch = { via: string; cards: Card[] };

const OPENING: Card[] = [
  {
    kind: "script-context",
    text: "Booked from the pricing page, downloaded the estimate guide two days ago. Do not quote a number before the scope question.",
  },
  {
    kind: "script-question",
    text: "Before I show you anything — what made you book this call today?",
  },
  {
    kind: "script-question",
    text: "What have you already tried to fix that, and where did it fall over?",
  },
  {
    kind: "script-transition",
    text: "Let me show you what this looks like when it is working, then we can talk about whether it fits.",
  },
  {
    kind: "script-action",
    text: "Share screen. Open their funnel map and walk the three steps they described.",
  },
  {
    kind: "script-question",
    text: "Does that fit what you had in mind — budget included?",
  },
];

const BRANCHES: Branch[] = [
  {
    via: "Looks right",
    cards: [
      {
        kind: "script-statement",
        text: "Then the only thing left is getting you started before the next cycle.",
      },
      {
        kind: "script-action",
        text: "Send the agreement link while still on the call. Do not end the call first.",
      },
    ],
  },
  {
    via: "Too expensive",
    cards: [
      {
        kind: "script-context",
        text: "Do not discount. Cut scope or move to the payment plan — a discount here trains every future renewal.",
      },
      {
        kind: "script-question",
        text: "Is it the total, or the timing of the payments?",
      },
    ],
  },
  {
    via: "Need to ask someone",
    cards: [
      {
        kind: "script-question",
        text: "Who else needs to see this, and what is the first thing they will ask you?",
      },
      {
        kind: "script-action",
        text: "Book the follow-up with both of them on the call, now.",
      },
    ],
  },
];

/** An illustrative recorded path — the shape a finished run has. */
const RUN_PATH: { at: string; kind: ScriptItemKind; text: string; via?: string }[] =
  [
    { at: "0:00", kind: "script-question", text: "What made you book this call today?" },
    { at: "0:48", kind: "script-question", text: "What have you already tried?" },
    { at: "3:10", kind: "script-transition", text: "Let me show you what this looks like…" },
    { at: "3:25", kind: "script-action", text: "Share screen, open their funnel map" },
    { at: "9:02", kind: "script-question", text: "Does that fit — budget included?" },
    {
      at: "10:15",
      kind: "script-question",
      text: "Is it the total, or the timing of the payments?",
      via: "Too expensive",
    },
    { at: "13:40", kind: "script-action", text: "Send the payment-plan link" },
  ];

const STEPS = [
  {
    title: "Drop a Sales Script on the map",
    body: "It is a container on the same canvas as your automations — so the call sits between the sequence that booked it and the one that follows it up.",
  },
  {
    title: "Fill it with cards",
    body: "One move per card: a question to ask, a line to say, a bridge to the next section, something to do, or a note the rep reads but never says.",
  },
  {
    title: "Label the branches",
    body: "Draw an arrow out of a question for each answer you actually hear and label it. The label is how the call gets recorded later.",
  },
  {
    title: "Press Run on a live call",
    body: "Pick the contact, then click each card as you use it. Ending the run writes the whole path to the contact and fires the follow-up automation.",
  },
];

const FAQ = [
  {
    q: "Can two reps run the same sales script at the same time?",
    a: "Yes. Every run is its own record, owned by the rep who started it — two people running the same script on two calls never share state, and nobody can write into somebody else's live call.",
  },
  {
    q: "Does FrazzleCRM listen to or transcribe the call?",
    a: "No. There is no recording, no transcription and no AI listening in. The record is built from the cards the rep clicks while they talk, which is why it stays accurate about what was covered and says nothing about how it was said.",
  },
  {
    q: "What happens when a call ends?",
    a: "The full path — every card, in order, with the branch labels it went through — is saved to the contact's timeline, and the “Sales script call ends” trigger fires. That trigger can be scoped to scripts whose name contains a word, so a discovery-call script and a renewal-call script can start different follow-up sequences.",
  },
  {
    q: "How many cards can one sales script have?",
    a: `A script is a container on the map, so it uses the same per-container node cap as any flow: ${PLAN_LIMITS.free.nodesPerFlow} cards on the Free plan, ${PLAN_LIMITS.starter.nodesPerFlow} on Starter, and unlimited on Pro, Scale and Founding Member. Ten cards is enough to try a script honestly and too few to run a real discovery call.`,
  },
  {
    q: "Do sales scripts use up my automation allowance?",
    a: "No. A script is never bound to an automation — it does not count against your plan's flow limit, and nothing inside a script sends an email or an SMS on its own.",
  },
];

function ScriptCard({ card }: { card: Card }) {
  const meta = SCRIPT_ITEM_META[card.kind];
  const isContext = card.kind === "script-context";
  return (
    <div
      className={`rounded-xl border bg-white p-3.5 shadow-sm ${
        isContext ? "border-dashed border-slate-300 bg-slate-50" : "border-slate-200"
      }`}
      style={{ borderLeft: `4px solid ${meta.color}` }}
    >
      <span
        className="inline-block rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide"
        style={{ background: meta.bg, color: meta.color }}
      >
        {meta.tag}
      </span>
      <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700">
        {card.text}
      </p>
    </div>
  );
}

function Connector() {
  return (
    <div aria-hidden className="mx-auto h-5 w-px bg-slate-200 sm:h-6" />
  );
}

export default function SalesScriptsPage() {
  const kinds = Object.keys(SCRIPT_ITEM_META) as ScriptItemKind[];

  return (
    <SiteShell
      title="Sales call scripts as flow charts, with live call tracking — FrazzleCRM"
      description="Build your sales call script as a branching flow chart, then run a live call from it. Every question, branch and objection the call went through is recorded on the contact — and ending the call starts the follow-up automation."
      canonical={`${SITE_URL}/sales-scripts`}
      ogTitle="Your sales script, as a flow chart your reps actually click through"
    >
      <JsonLd data={salesScriptHowToLd()} />
      <JsonLd data={faqLd(FAQ)} />

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-3xl px-6 pb-10 pt-14 text-center sm:pt-16">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-white px-3 py-1 text-[12px] font-semibold text-indigo-700">
            Sales Scripts
          </div>
          <h1 className="text-[32px] font-extrabold leading-[1.12] tracking-tight text-slate-900 sm:text-[44px]">
            Your call script, as a{" "}
            <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              flow chart
            </span>{" "}
            your reps click through
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            A sales script is a tree, not a document. Draw it on the same
            canvas as your funnel, press Run when the call starts, and the
            route the conversation actually took is recorded against the
            contact — objection and all.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a
              href={`${APP_URL}/signup`}
              className="rounded-xl bg-indigo-600 px-6 py-3 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
            >
              Build one free
            </a>
            <Link
              href="/templates/discovery-call-application"
              className="rounded-xl border border-slate-200 bg-white px-6 py-3 text-[15px] font-semibold text-slate-700 hover:bg-slate-50"
            >
              Start from the discovery-call map
            </Link>
          </div>
        </div>
      </section>

      {/* The script itself */}
      <section className="mx-auto max-w-5xl px-6 py-10">
        <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
          What a script looks like
        </h2>
        <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-slate-600">
          One move per card. The rep sees the two or three cards that come
          next, not four pages of prose — and the answers that change the call
          are drawn as branches instead of buried in brackets.
        </p>

        <div className="mt-7 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-7">
          <div className="mx-auto max-w-md">
            {OPENING.map((c, i) => (
              <React.Fragment key={c.text}>
                {i > 0 && <Connector />}
                <ScriptCard card={c} />
              </React.Fragment>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {BRANCHES.map((b) => (
              <div key={b.via}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="rounded-full border border-indigo-200 bg-white px-2.5 py-0.5 text-[11.5px] font-bold text-indigo-700">
                    {b.via}
                  </span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>
                <div className="space-y-3">
                  {b.cards.map((c) => (
                    <ScriptCard key={c.text} card={c} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-[12.5px] text-slate-400">
          An example discovery call, written for this page. The branch labels
          are the labels on the arrows — that is all a branch is.
        </p>
      </section>

      {/* Card types */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-5xl px-6 py-14">
          <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
            Five kinds of card
          </h2>
          <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-slate-600">
            The type is not decoration: it is how a rep skims. Say-this looks
            different from ask-this, and the notes they must not read aloud
            are dashed and grey.
          </p>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {kinds.map((k) => {
              const meta = SCRIPT_ITEM_META[k];
              return (
                <div
                  key={k}
                  className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
                  style={{ borderTop: `3px solid ${meta.color}` }}
                >
                  <span
                    className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide"
                    style={{ background: meta.bg, color: meta.color }}
                  >
                    {meta.tag}
                  </span>
                  <p className="mt-2.5 text-[13.5px] leading-relaxed text-slate-600">
                    {meta.hint}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Running a call */}
      <section className="mx-auto max-w-5xl px-6 py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
          <div>
            <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
              Press Run, and the call records itself
            </h2>
            <div className="mt-4 space-y-3.5 text-[14px] leading-relaxed text-slate-600">
              <p>
                Starting a run attaches the script to the contact you are
                calling. From then on, clicking a card is the record: the card,
                the time, and the branch label you arrived by.
              </p>
              <p>
                Misclicks are normal on a live call, so the last step can be
                taken back without leaving the call. A run started by accident
                can be discarded outright — it fires nothing and shows up
                nowhere.
              </p>
              <p>
                Two reps can run the same script on two calls at the same
                moment. Each run is its own record, owned by the rep who
                started it, and nobody can write into somebody else&apos;s
                call.
              </p>
              <p>
                When you end the run, the whole path lands on the contact
                timeline, next to their emails, appointments and automation
                history.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-[12.5px] font-bold text-slate-700">
                Discovery Call · Priya N.
              </span>
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                ended · 14:20
              </span>
            </div>
            <ol className="mt-4 space-y-2.5">
              {RUN_PATH.map((p) => {
                const meta = SCRIPT_ITEM_META[p.kind];
                return (
                  <li key={p.at}>
                    {p.via && (
                      <div className="mb-1.5 ml-11 inline-block rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10.5px] font-bold text-indigo-700">
                        via &ldquo;{p.via}&rdquo;
                      </div>
                    )}
                    <div className="flex items-start gap-3">
                      <span className="w-8 shrink-0 pt-0.5 text-right text-[11px] font-semibold tabular-nums text-slate-400">
                        {p.at}
                      </span>
                      <span
                        aria-hidden
                        className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                        style={{ background: meta.color }}
                      />
                      <span className="text-[13px] leading-snug text-slate-700">
                        {p.text}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ol>
            <p className="mt-4 border-t border-slate-100 pt-3 text-[12px] text-slate-400">
              What a finished run looks like on the contact. Seven cards, one
              objection, one branch taken.
            </p>
          </div>
        </div>
      </section>

      {/* Four steps */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-5xl px-6 py-14">
          <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
            Four steps
          </h2>
          <ol className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <li
                key={s.title}
                className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-[13px] font-bold text-white">
                  {i + 1}
                </span>
                <h3 className="mt-3 text-[15px] font-bold text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Trigger */}
      <section className="mx-auto max-w-5xl px-6 py-14">
        <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
          The call ends where the automation starts
        </h2>
        <p className="mt-3 max-w-2xl text-[14px] leading-relaxed text-slate-600">
          Ending a run fires the <strong>Sales script call ends</strong>{" "}
          trigger. The follow-up sequence starts from the call itself, not from
          a rep remembering to add a tag twenty minutes later — which is the
          step that actually goes missing in every CRM.
        </p>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            {
              t: "Scoped by script name",
              b: "The trigger can fire only for scripts whose name contains a word, so a discovery call and a renewal call start different sequences from the same map.",
            },
            {
              t: "Contact required",
              b: "A run with no contact attached — practice, a dry run — fires nothing. There is nobody to enrol, so nothing is enrolled.",
            },
            {
              t: "The map is the whole picture",
              b: "The script sits on the same canvas as the sequence that booked the call and the one that follows it. One diagram, from first touch to signature.",
            },
          ].map((c) => (
            <div
              key={c.t}
              className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
            >
              <h3 className="text-[15px] font-bold text-slate-900">{c.t}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
                {c.b}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Honest limits */}
      <section className="border-t border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
            Honest notes
          </h2>
          <div className="mt-5 space-y-4 text-[14px] leading-relaxed text-slate-600">
            <p>
              <strong className="font-semibold text-slate-800">
                Nothing listens to the call.
              </strong>{" "}
              No recording, no transcription, no AI in the room. The record is
              built from what the rep clicks, which means it is honest about
              what was covered and silent about how it was said. If you want
              the call recorded, that is your dialer&apos;s job, not ours.
            </p>
            <p>
              <strong className="font-semibold text-slate-800">
                There is a ledger, not analytics.
              </strong>{" "}
              You get every script with its run count and last run, plus the
              full path of each call on the contact. What you cannot get yet is
              the aggregate — how many calls took the{" "}
              <em>too expensive</em> branch this month. That is the obvious
              next thing to build, and it is not built.
            </p>
            <p>
              <strong className="font-semibold text-slate-800">
                Card counts follow your plan.
              </strong>{" "}
              A script is a container on the map, so it uses the same node cap
              as a flow: {PLAN_LIMITS.free.nodesPerFlow} cards on Free,{" "}
              {PLAN_LIMITS.starter.nodesPerFlow} on Starter, unlimited from Pro
              up. Ten is enough to try the idea and too few for a real
              discovery call.
            </p>
            <p>
              <strong className="font-semibold text-slate-800">
                Share a map, keep the script.
              </strong>{" "}
              Public share links and embeds strip the text out of every script
              card, exactly as they strip email and SMS content. A prospect
              sees that a call happens there; they do not read your words.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 py-14">
        <h2 className="text-[22px] font-bold tracking-tight text-slate-900">
          Common questions
        </h2>
        <div className="mt-5 space-y-4">
          {FAQ.map((f) => (
            <div
              key={f.q}
              className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
            >
              <h3 className="text-[15px] font-bold text-slate-900">{f.q}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">
                {f.a}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA + cross-links */}
      <section className="mx-auto max-w-3xl px-6 pb-20">
        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-7 text-center">
          <h2 className="text-[20px] font-bold text-slate-900">
            Draw the call, then draw what happens after it
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-[14px] leading-relaxed text-slate-600">
            Sales Scripts live on the same canvas as your automations, on every
            plan including the free one. No credit card.
          </p>
          <a
            href={`${APP_URL}/signup`}
            className="mt-5 inline-block rounded-xl bg-indigo-600 px-6 py-3 text-[14px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Start free
          </a>
        </div>

        <p className="mt-8 text-[13.5px] leading-relaxed text-slate-500">
          Read the plain-English definition of a{" "}
          <Link
            href="/learn/sales-script"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            sales script
          </Link>
          , clone the{" "}
          <Link
            href="/templates/discovery-call-application"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            Discovery Call Application
          </Link>{" "}
          map that feeds one, see how it fits{" "}
          <Link
            href="/for/coaches"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            coaches
          </Link>{" "}
          and{" "}
          <Link
            href="/for/agencies"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            agencies
          </Link>
          , or kill the no-shows before the call with{" "}
          <Link
            href="/templates/appointment-reminders"
            className="font-medium text-indigo-600 hover:text-indigo-500"
          >
            appointment reminders
          </Link>
          .
        </p>
      </section>
    </SiteShell>
  );
}
